import globalAxios, {
    AxiosInstance,
    AxiosPromise,
    AxiosRequestConfig,
    AxiosResponse,
} from 'axios';

import { BASE_CHATBLK_API_PATH } from '../constants';

import {
    ButtonTypeMap,
    ChatblkFeedbackAnswerQualityRatingMap,
    ChatblkFeedbackNotificationTypeMap,
    ChatblkFeedbackQuestionCategoryMap,
    ChatblkFeedbackRelatedQuestionsQualityRatingMap,
    ChatblkLaunchModeMap,
    ChatblkMfeFunctionMap,
    ChatBlkPermsApiInterface,
    IconStateMap,
    IconTypeMap,
    RequestArgs,
} from './api.interfaces';
import { Configuration } from './configuration';

/**
 *
 * @export
 * @class BaseAPI
 */
export class BaseAPI {
    protected configuration: Configuration | undefined;

    constructor(
        configuration?: Configuration,
        protected basePath: string = BASE_CHATBLK_API_PATH,
        protected axios: AxiosInstance = globalAxios
    ) {
        if (configuration) {
            this.configuration = configuration;
            this.basePath = configuration.basePath || this.basePath;
        }
    }
}

/**
 *
 * @export
 */
export const setSearchParams = function (url: URL, ...objects: Array<object>) {
    const searchParams = new URLSearchParams(url.search);
    for (const object of objects) {
        for (const key in object) {
            if (Array.isArray(object[key])) {
                searchParams.delete(key);
                for (const item of object[key]) {
                    searchParams.append(key, item);
                }
            } else {
                searchParams.set(key, object[key]);
            }
        }
    }
    url.search = searchParams.toString();
};

/**
 *
 * @export
 */
export const createRequestFunction = function (
    axiosArgs: RequestArgs,
    globalAxios: AxiosInstance,
    BASE_PATH: string,
    configuration?: Configuration
) {
    return <T = object, R = AxiosResponse<T>>(
        axios: AxiosInstance = globalAxios,
        basePath: string = BASE_PATH
    ) => {
        const axiosRequestArgs = {
            ...axiosArgs.options,
            url: (configuration?.basePath || basePath) + axiosArgs.url,
        };
        return axios.request<T, R>(axiosRequestArgs);
    };
};

/**
 *
 * @export
 */
export const DUMMY_BASE_URL = 'https://example.com';

/**
 *
 * @export
 */
export const toPathString = function (url: URL) {
    return url.pathname + url.search + url.hash;
};

/**
 *  ChatBlkPermsApiAxiosParamCreator - axios parameter creator
 * @export
 */
export const ChatBlkPermsApiAxiosParamCreator = function (
    configuration?: Configuration
) {
    return {
        /**
         *
         * @summary Get user authorization to see UI
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        hasChatAccess: async (
            options: AxiosRequestConfig = {}
        ): Promise<RequestArgs> => {
            const localVarPath = `/axf-ai-manager/auth`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = {
                method: 'GET',
                ...baseOptions,
                ...options,
            };
            const localVarHeaderParameter = {} as object;
            const localVarQueryParameter = {} as object;

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            const headersFromBaseOptions = baseOptions?.headers ?? {};
            localVarRequestOptions.headers = {
                ...localVarHeaderParameter,
                ...headersFromBaseOptions,
                ...options.headers,
            };

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    };
};

/**
 * ChatBlkPermsApiFp - functional programming interface
 * @export
 */
export const ChatBlkPermsApiFp = function (configuration?: Configuration) {
    const localVarAxiosParamCreator =
        ChatBlkPermsApiAxiosParamCreator(configuration);
    return {
        /**
         *
         * @summary Get user authorization to see UI
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async hasChatAccess(
            options?: AxiosRequestConfig
        ): Promise<
            (axios?: AxiosInstance, basePath?: string) => AxiosPromise<boolean>
        > {
            const localVarAxiosArgs =
                await localVarAxiosParamCreator.hasChatAccess(options);
            return createRequestFunction(
                localVarAxiosArgs,
                globalAxios,
                this.basePath ?? BASE_CHATBLK_API_PATH,
                configuration
            );
        },
    };
};

/**
 *  ChatBlkPermsApiFactory - factory interface
 * @export
 */
export const ChatBlkPermsApiFactory = function (
    configuration?: Configuration,
    basePath?: string,
    axios?: AxiosInstance
) {
    const localVarFp = ChatBlkPermsApiFp(configuration);
    return {
        /**
         *
         * @summary Get user authorization to see UI
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async hasChatAccess(options?: object): AxiosPromise<boolean> {
            return localVarFp
                .hasChatAccess(options)
                .then((request) => request(axios, basePath));
        },
    };
};

/**
 * ChatBlkPermsApi - object-oriented interface
 * @export
 * @class ChatBlkPermsApi
 * @extends {BaseAPI}
 */
export class ChatBlkPermsApi
    extends BaseAPI
    implements ChatBlkPermsApiInterface
{
    /**
     *
     * @summary Get user authorization to see UI
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ChatBlkPermsApi
     */
    public async hasChatAccess(options?: AxiosRequestConfig) {
        return ChatBlkPermsApiFp(this.configuration)
            .hasChatAccess(options)
            .then((request) => request(this.axios, this.basePath));
    }
}

export const ChatblkFeedbackNotificationType: ChatblkFeedbackNotificationTypeMap =
    {
        CHATBLK_FEEDBACK_NOTIFICATION_TYPE_UNSPECIFIED: 0,
        CHATBLK_FEEDBACK_NOTIFICATION_TYPE_CREATE: 1,
        CHATBLK_FEEDBACK_NOTIFICATION_TYPE_DELETE: 2,
        CHATBLK_FEEDBACK_NOTIFICATION_TYPE_UPDATE: 3,
        CHATBLK_FEEDBACK_NOTIFICATION_TYPE_APPROVE: 4,
    };

export const ChatblkFeedbackQuestionCategory: ChatblkFeedbackQuestionCategoryMap =
    {
        CHATBLK_FEEDBACK_QUESTION_CATEGORY_UNSPECIFIED: 0,
        CHATBLK_FEEDBACK_QUESTION_CATEGORY_INSIGHT: 1,
        CHATBLK_FEEDBACK_QUESTION_CATEGORY_PRODUCT: 2,
        CHATBLK_FEEDBACK_QUESTION_CATEGORY_OUT_OF_SCOPE: 3,
    };

export const ChatblkFeedbackAnswerQualityRating: ChatblkFeedbackAnswerQualityRatingMap =
    {
        CHATBLK_FEEDBACK_ANSWER_QUALITY_RATING_UNSPECIFIED: 0,
        CHATBLK_FEEDBACK_ANSWER_QUALITY_RATING_NOT_AT_ALL_ACCURATE: 1,
        CHATBLK_FEEDBACK_ANSWER_QUALITY_RATING_NOT_VERY_ACCURATE: 2,
        CHATBLK_FEEDBACK_ANSWER_QUALITY_RATING_SOMEWHAT_ACCURATE: 3,
        CHATBLK_FEEDBACK_ANSWER_QUALITY_RATING_VERY_ACCURATE: 4,
        CHATBLK_FEEDBACK_ANSWER_QUALITY_RATING_EXTREMELY_ACCURATE: 5,
    };

export const ChatblkFeedbackRelatedQuestionsQualityRating: ChatblkFeedbackRelatedQuestionsQualityRatingMap =
    {
        CHATBLK_FEEDBACK_RELATED_QUESTIONS_QUALITY_RATING_UNSPECIFIED: 0,
        CHATBLK_FEEDBACK_RELATED_QUESTIONS_QUALITY_RATING_NOT_AT_ALL_ACCURATE: 1,
        CHATBLK_FEEDBACK_RELATED_QUESTIONS_QUALITY_RATING_NOT_VERY_ACCURATE: 2,
        CHATBLK_FEEDBACK_RELATED_QUESTIONS_QUALITY_RATING_SOMEWHAT_ACCURATE: 3,
        CHATBLK_FEEDBACK_RELATED_QUESTIONS_QUALITY_RATING_VERY_ACCURATE: 4,
        CHATBLK_FEEDBACK_RELATED_QUESTIONS_QUALITY_RATING_EXTREMELY_ACCURATE: 5,
    };

export const ButtonType: ButtonTypeMap = {
    BUTTON_TYPE_UNSPECIFIED: 0,
    BUTTON_TYPE_PRIMARY: 1,
    BUTTON_TYPE_SECONDARY: 2,
    BUTTON_TYPE_TERTIARY: 3,
    BUTTON_TYPE_TERTIARY_SUBTLE: 4,
    BUTTON_TYPE_DESTRUCTIVE_PRIMARY: 5,
    BUTTON_TYPE_DESTRUCTIVE_SECONDARY: 6,
    BUTTON_TYPE_DESTRUCTIVE_TERTIARY: 7,
    BUTTON_TYPE_AFFIRMATIVE_PRIMARY: 8,
    BUTTON_TYPE_AFFIRMATIVE_SECONDARY: 9,
    BUTTON_TYPE_AFFIRMATIVE_TERTIARY: 10,
};

export const IconType: IconTypeMap = {
    ICON_TYPE_UNSPECIFIED: 0,
    ICON_TYPE_FILL: 1,
    ICON_TYPE_TEXT: 2,
    ICON_TYPE_API: 3,
    ICON_TYPE_ACCOUNT: 4,
    ICON_TYPE_ACTION_12: 5,
    ICON_TYPE_ACTION: 6,
    ICON_TYPE_ACTIONARROW_BACK: 7,
    ICON_TYPE_ACTIONARROW_DOWN: 8,
    ICON_TYPE_ACTIONARROW_FORWARD: 9,
    ICON_TYPE_ACTIONARROW_UP: 10,
    ICON_TYPE_ADD: 11,
    ICON_TYPE_ALERT_2: 12,
    ICON_TYPE_ALERT_BELL_SUBTLE: 13,
    ICON_TYPE_ALERT_BELL: 14,
    ICON_TYPE_ALERT_BOLD_LARGE: 15,
    ICON_TYPE_ALERT_BOLD: 16,
    ICON_TYPE_SUBTLE_LARGE: 17,
    ICON_TYPE_SUBTLE: 18,
    ICON_TYPE_ALERT: 19,
    ICON_TYPE_ALIGN_CENTER: 20,
    ICON_TYPE_ALIGN_LEFT: 21,
    ICON_TYPE_ALIGN_RIGHT: 22,
    ICON_TYPE_ANALYSIS_RESULT: 23,
    ICON_TYPE_ARROW_DOWN: 24,
    ICON_TYPE_ARROW_DROPDOWN_UP: 25,
    ICON_TYPE_ARROW_DROPDOWN: 26,
    ICON_TYPE_ARROW_LEFT: 27,
    ICON_TYPE_ARROW_RIGHT: 28,
    ICON_TYPE_ARROW_UP: 29,
    ICON_TYPE_BACK_ARROW: 30,
    ICON_TYPE_BOOKMARK_BOLD: 31,
    ICON_TYPE_BOOKMARK_SUBTLE: 32,
    ICON_TYPE_BRIEFCASE: 33,
    ICON_TYPE_BUY: 34,
    ICON_TYPE_BXF_ARROW_RIGHT: 35,
    ICON_TYPE_BXF_BETA: 36,
    ICON_TYPE_BXF_CALENDAR: 37,
    ICON_TYPE_BXF_CAUTION: 38,
    ICON_TYPE_BXF_CAUTION_48: 39,
    ICON_TYPE_BXF_CHAT_BLK_BETA: 40,
    ICON_TYPE_BXF_CHAT: 41,
    ICON_TYPE_BXF_CLEAR: 42,
    ICON_TYPE_BXF_CLOSE: 43,
    ICON_TYPE_BXF_COPY: 44,
    ICON_TYPE_BXF_CONTACT: 45,
    ICON_TYPE_BXF_DISMISS: 46,
    ICON_TYPE_BXF_DOUBLE_DOWN: 47,
    ICON_TYPE_BXF_DOUBLE_UP: 48,
    ICON_TYPE_BXF_EMAIL: 49,
    ICON_TYPE_BXF_HISTORY: 50,
    ICON_TYPE_BXF_LINK_EXTERNAL: 51,
    ICON_TYPE_BXF_LOADING: 52,
    ICON_TYPE_BXF_LOADING48: 53,
    ICON_TYPE_BXF_LOGO_LARGE: 54,
    ICON_TYPE_BXF_NAV_ACTIVE: 55,
    ICON_TYPE_BXF_NAV_INACTIVE: 56,
    ICON_TYPE_BXF_NEW_CHAT: 57,
    ICON_TYPE_BXF_PDF: 58,
    ICON_TYPE_BXF_PHONE: 59,
    ICON_TYPE_BXF_FULL_SCREEN: 60,
    ICON_TYPE_BXF_EXIT_FULL_SCREEN: 61,
    ICON_TYPE_BXF_SEND: 62,
    ICON_TYPE_BXF_KEBAB_MENU: 63,
    ICON_TYPE_BXF_SPARKLE_LARGE: 64,
    ICON_TYPE_BXF_SPARKLE: 65,
    ICON_TYPE_BXF_STOP: 66,
    ICON_TYPE_BXF_THUMBS_DOWN: 67,
    ICON_TYPE_BXF_THUMBS_UP: 68,
    ICON_TYPE_CALENDAR: 69,
    ICON_TYPE_CARDS: 70,
    ICON_TYPE_CHAT_ATTACHMENT: 71,
    ICON_TYPE_CHAT_MICROPHONE: 72,
    ICON_TYPE_CHAT_NEW: 73,
    ICON_TYPE_CHAT_SUBMIT: 74,
    ICON_TYPE_CHECKBOX_MIXED_STATE_2: 75,
    ICON_TYPE_CHECKMARK: 76,
    ICON_TYPE_CLEAR: 77,
    ICON_TYPE_CLONE: 78,
    ICON_TYPE_COPILOT_20: 79,
    ICON_TYPE_COPILOT_32: 80,
    ICON_TYPE_COPILOT_64: 81,
    ICON_TYPE_COPILOT: 82,
    ICON_TYPE_COPY_TO_CLIPBOARD: 83,
    ICON_TYPE_DATA_CUSTOM_SORT: 84,
    ICON_TYPE_DATA_FILTER_A_Z: 85,
    ICON_TYPE_DATA_FILTER_LARGE_SMALL: 86,
    ICON_TYPE_DATA_FILTER_SMALL_LARGE: 87,
    ICON_TYPE_DATA_FILTER_Z_A: 88,
    ICON_TYPE_DATA_SELECT_ARROW_DOWN: 89,
    ICON_TYPE_DATA_SELECT_ARROW_UP: 90,
    ICON_TYPE_DATA_SELECT_CARET_DOWN: 91,
    ICON_TYPE_DATA_SELECT_CARET_UP: 92,
    ICON_TYPE_DELETE_SMALL: 93,
    ICON_TYPE_DELETE_TRASH: 94,
    ICON_TYPE_DELETE: 95,
    ICON_TYPE_DETAIL: 96,
    ICON_TYPE_DID_NOT_RUN: 97,
    ICON_TYPE_DOWNLOAD: 98,
    ICON_TYPE_DRAW_ARROW: 99,
    ICON_TYPE_DRAW_ELLIPSE: 100,
    ICON_TYPE_DRAW_FREEFORM: 101,
    ICON_TYPE_DRAW_RECTANGLE: 102,
    ICON_TYPE_DRAWER_LEFT: 103,
    ICON_TYPE_DRAWER_RIGHT: 104,
    ICON_TYPE_DUPLICATE: 105,
    ICON_TYPE_EDIT: 106,
    ICON_TYPE_ELLIPSIS: 107,
    ICON_TYPE_ERROR_BOLD: 108,
    ICON_TYPE_ERROR_SUBTLE_2: 109,
    ICON_TYPE_ERROR_SUBTLE: 110,
    ICON_TYPE_EXCEL: 111,
    ICON_TYPE_EXPORT: 112,
    ICON_TYPE_EYE: 113,
    ICON_TYPE_FAVORITE_BOLD_32: 114,
    ICON_TYPE_FAVORITE_BOLD: 115,
    ICON_TYPE_FAVORITE_SUBTLE: 116,
    ICON_TYPE_FILE: 117,
    ICON_TYPE_FILTER_SUBTLE: 118,
    ICON_TYPE_FILTER: 119,
    ICON_TYPE_FOLDER_SUBTLE: 120,
    ICON_TYPE_FOLDER: 121,
    ICON_TYPE_GLOBE: 122,
    ICON_TYPE_GRIPPER: 123,
    ICON_TYPE_HELP_BOLD: 124,
    ICON_TYPE_HELP_SUBTLE: 125,
    ICON_TYPE_HELP: 126,
    ICON_TYPE_ICON_DETAIL: 127,
    ICON_TYPE_ICON_LIST: 128,
    ICON_TYPE_ICON_WIDGET_TABLE: 129,
    ICON_TYPE_ICON_WIDGET_VISUALIZATION: 130,
    ICON_TYPE_IMPORTANT_QUOTES: 131,
    ICON_TYPE_INFO_2: 132,
    ICON_TYPE_INFO_BOLD_LARGE: 133,
    ICON_TYPE_INFO_BOLD: 134,
    ICON_TYPE_INFO_SUBTLE_LARGE: 135,
    ICON_TYPE_INFO_SUBTLE: 136,
    ICON_TYPE_INFO: 137,
    ICON_TYPE_INPROGRESS_2: 138,
    ICON_TYPE_INPROGRESS_BOLD_LARGE: 139,
    ICON_TYPE_INPROGRESS_BOLD: 140,
    ICON_TYPE_INPROGRESS_SUBTLE_LARGE: 141,
    ICON_TYPE_INPROGRESS_SUBTLE: 142,
    ICON_TYPE_LAUNCH: 143,
    ICON_TYPE_LAYOUT: 144,
    ICON_TYPE_LIKE: 145,
    ICON_TYPE_LINK: 146,
    ICON_TYPE_LIST: 147,
    ICON_TYPE_LIVECHAT: 148,
    ICON_TYPE_LOADING: 149,
    ICON_TYPE_LOCK: 150,
    ICON_TYPE_LOG_IN: 151,
    ICON_TYPE_LOG_OUT: 152,
    ICON_TYPE_MAIL: 153,
    ICON_TYPE_MAXIMIZE: 154,
    ICON_TYPE_MENU: 155,
    ICON_TYPE_MERGE: 156,
    ICON_TYPE_MINE: 157,
    ICON_TYPE_MINIMIZE: 158,
    ICON_TYPE_MONITOR: 159,
    ICON_TYPE_MORE_OPTIONS: 160,
    ICON_TYPE_MOVE_ALL_DOWN: 161,
    ICON_TYPE_MOVE_ALL_LEFT: 162,
    ICON_TYPE_MOVE_ALL_RIGHT: 163,
    ICON_TYPE_MOVE_ALL_SELECTED: 164,
    ICON_TYPE_MOVE_ALL_UNSELECTED: 165,
    ICON_TYPE_MOVE_ALL_UP: 166,
    ICON_TYPE_MOVE_FOLDER: 167,
    ICON_TYPE_NESTED: 168,
    ICON_TYPE_NEW_2: 169,
    ICON_TYPE_NEW_3: 170,
    ICON_TYPE_NEW_BOLD_LARGE: 171,
    ICON_TYPE_NEW_BOLD: 172,
    ICON_TYPE_NEW_SUBTLE_LARGE: 173,
    ICON_TYPE_NEW_SUBTLE: 174,
    ICON_TYPE_OPEN_ORDER: 175,
    ICON_TYPE_PAUSE: 176,
    ICON_TYPE_PHONE: 177,
    ICON_TYPE_PIN: 178,
    ICON_TYPE_PLAY: 179,
    ICON_TYPE_POPIN: 180,
    ICON_TYPE_POPOUT: 181,
    ICON_TYPE_PRINT: 182,
    ICON_TYPE_PROGRESS_ACTIVE_LARGE: 183,
    ICON_TYPE_PROGRESS_ACTIVE: 184,
    ICON_TYPE_PROGRESS_EMPTY_LARGE: 185,
    ICON_TYPE_PROGRESS_EMPTY: 186,
    ICON_TYPE_REDO: 187,
    ICON_TYPE_REFRESH: 188,
    ICON_TYPE_RELOAD: 189,
    ICON_TYPE_REMOVE: 190,
    ICON_TYPE_ROUTES: 191,
    ICON_TYPE_ROUTING_CONTROLS: 192,
    ICON_TYPE_RUNNING: 193,
    ICON_TYPE_SAVE: 194,
    ICON_TYPE_SEARCH: 195,
    ICON_TYPE_SELL: 196,
    ICON_TYPE_SETTINGS_SUBTLE: 197,
    ICON_TYPE_SETTINGS: 198,
    ICON_TYPE_SHARE: 199,
    ICON_TYPE_SIGMA: 200,
    ICON_TYPE_SPLIT: 201,
    ICON_TYPE_STEERING: 202,
    ICON_TYPE_STEPPER_DOWN: 203,
    ICON_TYPE_STEPPER_UP: 204,
    ICON_TYPE_SUCCESS_BOLD_LARGE: 205,
    ICON_TYPE_SUCCESS_BOLD: 206,
    ICON_TYPE_SUCCESS_SUBTLE_LARGE: 207,
    ICON_TYPE_SUCCESS_SUBTLE: 208,
    ICON_TYPE_TABLE: 209,
    ICON_TYPE_TEAM: 210,
    ICON_TYPE_TEMPLATE_LOADED: 211,
    ICON_TYPE_TERMINATE: 212,
    ICON_TYPE_TEXT_BOLD: 213,
    ICON_TYPE_TEXT_ITALIC: 214,
    ICON_TYPE_TEXT_UNDERLINE: 215,
    ICON_TYPE_THUMBS_DOWN: 216,
    ICON_TYPE_TICKER_NEGATIVE: 217,
    ICON_TYPE_TICKER_POSITIVE: 218,
    ICON_TYPE_UNDO: 219,
    ICON_TYPE_UNLOCK: 220,
    ICON_TYPE_UP_DOWN: 221,
    ICON_TYPE_UPLOAD: 222,
    ICON_TYPE_UPLOADER_32: 223,
    ICON_TYPE_UPLOADER_68: 224,
    ICON_TYPE_USE_WORKFLOW_COLORS: 225,
    ICON_TYPE_VISUALIZATION: 226,
    ICON_TYPE_WAITING: 227,
    ICON_TYPE_WIDGET_DELETE: 228,
    ICON_TYPE_WIDGET_DOWNLOAD: 229,
    ICON_TYPE_WIDGET_DUPLICATE: 230,
    ICON_TYPE_WIDGET_ELLIPSIS: 231,
    ICON_TYPE_WIDGET_EXPORT: 232,
    ICON_TYPE_WIDGET_FILTER: 233,
    ICON_TYPE_WIDGET_FOOTNOTE: 234,
    ICON_TYPE_WIDGET_LINK: 235,
    ICON_TYPE_WIDGET_MAXIMIZE: 236,
    ICON_TYPE_WIDGET_MINIMIZE: 237,
    ICON_TYPE_WIDGET_PIN: 238,
    ICON_TYPE_WIDGET_POPOUT: 239,
    ICON_TYPE_WIDGET_REFRESH: 240,
    ICON_TYPE_WIDGET_SAVE: 241,
    ICON_TYPE_WIDGET_SETTINGS: 242,
    ICON_TYPE_WIDGET_SIZE_EXPAND_GROW: 243,
    ICON_TYPE_WIDGET_SIZE_EXPAND_SHRINK: 244,
    ICON_TYPE_WIDGET_SIZE_MAX: 245,
    ICON_TYPE_WIDGET_SIZE_SMALLER: 246,
    ICON_TYPE_WIDGET_TABLE: 247,
    ICON_TYPE_WIDGET_UPLOAD: 248,
    ICON_TYPE_WIDGET_VISUALIZATION: 249,
    ICON_TYPE_ZOOM_IN: 250,
    ICON_TYPE_ZOOM_OUT: 251,
};

export const IconState: IconStateMap = {
    ICON_STATE_UNSPECIFIED: 0,
    ICON_STATE_PRIMARY: 1,
    ICON_STATE_PRIMARY_HOVER: 2,
    ICON_STATE_PRIMARY_NON_ACTION: 3,
    ICON_STATE_SECONDARY: 4,
    ICON_STATE_SECONDARY_ACTIVE: 5,
    ICON_STATE_NON_ACTION: 6,
    ICON_STATE_BOXED: 7,
    ICON_STATE_BOXED_HOVER: 8,
    ICON_STATE_SUCCESS: 9,
    ICON_STATE_SUCCESS_SUBTLE: 10,
    ICON_STATE_GAIN: 11,
    ICON_STATE_SUCCESS_ALTERNATE: 12,
    ICON_STATE_ERROR: 13,
    ICON_STATE_ERROR_SUBTLE: 14,
    ICON_STATE_LOSS: 15,
    ICON_STATE_ALERT: 16,
    ICON_STATE_ALERT_SUBTLE: 17,
    ICON_STATE_WARNING: 18,
    ICON_STATE_WARNING_SUBTLE: 19,
    ICON_STATE_INFORMATIONAL: 20,
    ICON_STATE_INFORMATIONAL_SUBTLE: 21,
    ICON_STATE_NEW: 22,
    ICON_STATE_NEW_SUBTLE: 23,
    ICON_STATE_ATTENTION: 24,
    ICON_STATE_CHECKBOX: 25,
    ICON_STATE_NEUTRAL: 26,
    ICON_STATE_NEUTRAL_SUBTLE: 27,
    ICON_STATE_BXF_BUTTON: 28,
    ICON_STATE_PRIMARY_BUTTON: 29,
    ICON_STATE_PRIMARY_BUTTON_HOVER: 30,
    ICON_STATE_PRIMARY_BUTTON_ACTIVE: 31,
    ICON_STATE_SECONDARY_BUTTON: 32,
    ICON_STATE_SECONDARY_BUTTON_HOVER: 33,
    ICON_STATE_SECONDARY_BUTTON_ACTIVE: 34,
    ICON_STATE_TERTIARY_BUTTON: 35,
    ICON_STATE_TERTIARY_BUTTON_HOVER: 36,
    ICON_STATE_TERTIARY_BUTTON_ACTIVE: 37,
    ICON_STATE_TERTIARY_ALT_BUTTON: 38,
    ICON_STATE_TERTIARY_ALT_BUTTON_HOVER: 39,
    ICON_STATE_TERTIARY_ALT_BUTTON_ACTIVE: 40,
    ICON_STATE_TERTIARY_SUBTLE_BUTTON: 41,
    ICON_STATE_TERTIARY_SUBTLE_BUTTON_HOVER: 42,
    ICON_STATE_TERTIARY_BUTTON_ACTIVE_ALT: 43,
    ICON_STATE_TERTIARY_BUTTON_ACTIVE_HOVER: 44,
    ICON_STATE_TERTIARY_BUTTON_ACTIVE_ACTIVE: 45,
    ICON_STATE_TERTIARY_SUBTLE_BUTTON_ACTIVE: 46,
    ICON_STATE_DESTRUCTIVE_PRIMARY_BUTTON: 47,
    ICON_STATE_DESTRUCTIVE_PRIMARY_BUTTON_HOVER: 48,
    ICON_STATE_DESTRUCTIVE_PRIMARY_BUTTON_ACTIVE: 49,
    ICON_STATE_DESTRUCTIVE_SECONDARY_BUTTON: 50,
    ICON_STATE_DESTRUCTIVE_SECONDARY_BUTTON_HOVER: 51,
    ICON_STATE_DESTRUCTIVE_SECONDARY_BUTTON_ACTIVE: 52,
    ICON_STATE_DESTRUCTIVE_TERTIARY_BUTTON: 53,
    ICON_STATE_DESTRUCTIVE_TERTIARY_BUTTON_HOVER: 54,
    ICON_STATE_DESTRUCTIVE_TERTIARY_BUTTON_ACTIVE: 55,
    ICON_STATE_AFFIRMATIVE_PRIMARY_BUTTON: 56,
    ICON_STATE_AFFIRMATIVE_PRIMARY_BUTTON_HOVER: 57,
    ICON_STATE_AFFIRMATIVE_PRIMARY_BUTTON_ACTIVE: 58,
    ICON_STATE_AFFIRMATIVE_SECONDARY_BUTTON: 59,
    ICON_STATE_AFFIRMATIVE_SECONDARY_BUTTON_HOVER: 60,
    ICON_STATE_AFFIRMATIVE_SECONDARY_BUTTON_ACTIVE: 61,
    ICON_STATE_AFFIRMATIVE_TERTIARY_BUTTON: 62,
    ICON_STATE_AFFIRMATIVE_TERTIARY_BUTTON_HOVER: 63,
    ICON_STATE_AFFIRMATIVE_TERTIARY_BUTTON_ACTIVE: 64,
    ICON_STATE_UNDERLINE: 65,
    ICON_STATE_UNDERLINE_HOVER: 66,
};

export const ChatblkLaunchMode: ChatblkLaunchModeMap = {
    CHATBLK_LAUNCH_MODE_UNSPECIFIED: 0,
    CHATBLK_LAUNCH_MODE_STANDALONE_BROWSER: 1,
    CHATBLK_LAUNCH_MODE_STANDALONE_EBC: 2,
    CHATBLK_LAUNCH_MODE_EMBEDDED_WEB: 3,
    CHATBLK_LAUNCH_MODE_EMBEDDED_JAVA: 4,
};

export const ChatblkMfeFunction: ChatblkMfeFunctionMap = {
    CHATBLK_MFE_FUNCTION_UNSPECIFIED: 0,
    CHATBLK_MFE_FUNCTION_LAUNCH: 1,
    CHATBLK_MFE_FUNCTION_ERROR: 2,
    CHATBLK_MFE_FUNCTION_OPENED_FROM_HEADER: 3,
    CHATBLK_MFE_FUNCTION_OPENED_FROM_SUMMARIZE: 4,
    CHATBLK_MFE_FUNCTION_OPENED_FROM_RELATED_QUESTION: 5,
    CHATBLK_MFE_FUNCTION_CLOSED: 6,
    CHATBLK_MFE_FUNCTION_CANVAS_RESIZED: 10,
    CHATBLK_MFE_FUNCTION_SIGN_IN_CLICKED: 11,
    CHATBLK_MFE_FUNCTION_RECENT_CLICKED: 12,
    CHATBLK_MFE_FUNCTION_COPY_CLICKED: 13,
    CHATBLK_MFE_FUNCTION_METHODOLOGY_CLICKED: 14,
    CHATBLK_MFE_FUNCTION_IMPORTANT_INFO_CLICKED: 15,
    CHATBLK_MFE_FUNCTION_RESET_CLICKED: 16,
    CHATBLK_MFE_FUNCTION_RECENT_CLOSE_CLICKED: 17,
    CHATBLK_MFE_FUNCTION_RECENT_PROMPT_CLICKED: 18,
    CHATBLK_MFE_FUNCTION_IMPORTANT_INFO_CLOSE_CLICKED: 19,
    CHATBLK_MFE_FUNCTION_PROMPT_BOX_SUBMIT_BUTTON_CLICKED: 20,
    CHATBLK_MFE_FUNCTION_PROMPT_BOX_TEXT_AREA_CLICKED: 21,
    CHATBLK_MFE_FUNCTION_PROMPT_BOX_ATTACHMENT_ICON_CLICKED: 22,
    CHATBLK_MFE_FUNCTION_PROMPT_BOX_ATTACHMENT_ADDED: 23,
    CHATBLK_MFE_FUNCTION_PROMPT_BOX_ATTACHMENT_FAILED: 24,
    CHATBLK_MFE_FUNCTION_PROMPT_BOX_VOICE_INPUT_ICON_CLICKED: 25,
    CHATBLK_MFE_FUNCTION_PROMPT_BOX_RELATED_QUESTION_SUBMITTED: 26,
    CHATBLK_MFE_FUNCTION_PROMPT_BOX_SUMMARIZE_SUBMITTED: 27,
    CHATBLK_MFE_FUNCTION_QUERY_CANCELED: 30,
    CHATBLK_MFE_FUNCTION_QUERY_RESPONSE_LOADED: 31,
    CHATBLK_MFE_FUNCTION_QUERY_RESPONSE_LINK_CLICKED: 32,
    CHATBLK_MFE_FUNCTION_QUERY_RESPONSE_IMAGE_CLICKED: 33,
    CHATBLK_MFE_FUNCTION_QUERY_RESPONSE_BUTTON_CLICKED: 34,
    CHATBLK_MFE_FUNCTION_QUERY_RESPONSE_MFE: 35,
    CHATBLK_MFE_FUNCTION_CHAT_BUBBLE_FEEDBACK_SUBMITTED: 36,
    CHATBLK_MFE_FUNCTION_CHAT_BUBBLE_CLICKED: 40,
    CHATBLK_MFE_FUNCTION_CHAT_BUBBLE_DOWNLOAD_ICON_CLICKED: 41,
    CHATBLK_MFE_FUNCTION_CHAT_BUBBLE_COPY_ICON_CLICKED: 42,
    CHATBLK_MFE_FUNCTION_CHAT_BUBBLE_SOURCES_TOGGLE_CLICKED: 43,
    CHATBLK_MFE_FUNCTION_CHAT_BUBBLE_FOLLOW_UP_SUGGESTION_CLICKED: 44,
    CHATBLK_MFE_FUNCTION_CHAT_BUBBLE_WAS_THIS_HELPFUL_CLICKED: 45,
    CHATBLK_MFE_FUNCTION_CHAT_BUBBLE_FEEDBACK_EXPANDED: 46,
    CHATBLK_MFE_FUNCTION_CHAT_BUBBLE_SIMILAR_QUESTION_CLICKED: 47,
    CHATBLK_MFE_FUNCTION_ACTION_NEW_CHAT_ICON_CLICKED: 50,
    CHATBLK_MFE_FUNCTION_ACTION_CLOSE_ICON_CLICKED: 51,
    CHATBLK_MFE_FUNCTION_ACTION_MENU_ICON_CLICKED: 52,
    CHATBLK_MFE_FUNCTION_ACTION_MENU_OPTION_UNSPECIFIED: 53,
    CHATBLK_MFE_FUNCTION_ACTION_MENU_OPTION_COPY_CLICKED: 54,
    CHATBLK_MFE_FUNCTION_ACTION_MENU_OPTION_EXPORT_CLICKED: 55,
    CHATBLK_MFE_FUNCTION_ACTION_MENU_OPTION_CHAT_HISTORY_CLICKED: 56,
    CHATBLK_MFE_FUNCTION_ACTION_MENU_OPTION_SETTINGS_CLICKED: 57,
    CHATBLK_MFE_FUNCTION_ACTION_MENU_OPTION_FULLSCREEN_CLICKED: 58,
    CHATBLK_MFE_FUNCTION_ACTION_MENU_OPTION_CONTACT_RM_CLICKED: 59,
    CHATBLK_MFE_FUNCTION_SOURCES_POPOVER_OPENED: 60,
    CHATBLK_MFE_FUNCTION_SOURCES_POPOVER_CLOSED: 61,
    CHATBLK_MFE_FUNCTION_SOURCES_POPOVER_LINK_CLICKED: 62,
    CHATBLK_MFE_FUNCTION_SOURCES_POPOVER_ICON_CLICKED: 63,
    CHATBLK_MFE_FUNCTION_SOURCES_ACCORDION_EXPANDED: 70,
    CHATBLK_MFE_FUNCTION_SOURCES_ACCORDION_COLLAPSED: 71,
    CHATBLK_MFE_FUNCTION_SOURCES_ACCORDION_LINK_CLICKED: 72,
    CHATBLK_MFE_FUNCTION_RM_EMAIL_COPIED: 75,
    CHATBLK_MFE_FUNCTION_RM_PHONE_NUMBER_COPIED: 76,
    CHATBLK_MFE_FUNCTION_RM_SCHEDULE_MEETING_CLICKED: 77,
    CHATBLK_MFE_FUNCTION_INFO_POPOVER_OPENED: 80,
    CHATBLK_MFE_FUNCTION_INFO_POPOVER_CLOSED: 81,
    CHATBLK_MFE_FUNCTION_INFO_POPOVER_LINK_CLICKED: 82,
    CHATBLK_MFE_FUNCTION_INFO_POPOVER_ICON_CLICKED: 83,
};
